import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import Heading from "../../components/Heading";
import { useCreateAmbassador, useGetUserById, useSendAmbassadorCredentials, useUpdateAmbassador } from "../../queries/user";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ImageInput from "../../components/Forms/ImageInput";
import MediaUpload from "../../components/Forms/MediaUpload";
import { FormSection } from "./components/FormSection";
import { ActionButton } from "../../components/Forms/ActionButton";
import NewDropdown from '../../components/Forms/NewDropdown';
import countryStatesMapping from '../../data/countryStatesMapping';

export default function GreenAmbassadorForm() {

    const { state } = useLocation();
    const id = state?.userId;
    const navigate = useNavigate();

    const [active, setActive] = useState(false);
    const [image, setImage] = useState(null);

    // Country and State options
    const countryOptions = [
        { value: 'India', label: 'India' },
        { value: 'United States', label: 'United States' },
        { value: 'United Kingdom', label: 'United Kingdom' },
        { value: 'Canada', label: 'Canada' },
        { value: 'Australia', label: 'Australia' },
        { value: 'Germany', label: 'Germany' },
        { value: 'France', label: 'France' },
        { value: 'Japan', label: 'Japan' },
        { value: 'China', label: 'China' },
        { value: 'Brazil', label: 'Brazil' },
        { value: 'Mexico', label: 'Mexico' },
        { value: 'South Korea', label: 'South Korea' },
        { value: 'Italy', label: 'Italy' },
        { value: 'Spain', label: 'Spain' },
        { value: 'Netherlands', label: 'Netherlands' },
        { value: 'Sweden', label: 'Sweden' },
        { value: 'Norway', label: 'Norway' },
        { value: 'Denmark', label: 'Denmark' },
        { value: 'Finland', label: 'Finland' },
        { value: 'Singapore', label: 'Singapore' },
        { value: 'New Zealand', label: 'New Zealand' },
        { value: 'South Africa', label: 'South Africa' },
        { value: 'Argentina', label: 'Argentina' },
        { value: 'Chile', label: 'Chile' },
        { value: 'Colombia', label: 'Colombia' },
        { value: 'Peru', label: 'Peru' },
        { value: 'Venezuela', label: 'Venezuela' },
        { value: 'Ecuador', label: 'Ecuador' },
        { value: 'Uruguay', label: 'Uruguay' },
        { value: 'Paraguay', label: 'Paraguay' },
        { value: 'Bolivia', label: 'Bolivia' },
        { value: 'Guyana', label: 'Guyana' },
        { value: 'Suriname', label: 'Suriname' },
        { value: 'French Guiana', label: 'French Guiana' },
        { value: 'Other', label: 'Other' },
    ];

    // Use the shared country-states mapping from central data file
    const stateOptionsByCountry = countryStatesMapping;

    const { mutateAsync: create, isPending: isCreating } = useCreateAmbassador();
    const { mutateAsync: saveAndSendCredentials, isPending: isSendingCredentials } = useSendAmbassadorCredentials();
    const { mutateAsync: updateAmbassador, isPending: isUpdatingAmbassador } = useUpdateAmbassador(id);
    const { data: userData, isLoading: isLoadingUser } = useGetUserById(id);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        reset,
        getValues,
        setError,
        clearErrors,
        setValue,
    } = useForm({
        defaultValues: {
            firstName: "",
            lastName: "",
            username: "",
            email: "",
            emailRecipients: "",
            mobile: "",
            dob: "",
            gender: "",
            country: "",
            state: "",
            city: "",
            address: "",
            pincode: "",
            landmark: "",
            contactPersonName: "",
            contactPersonEmail: "",
            contactPersonPhone: "",
            linkedIn: "",
            instagram: "",
            facebook: "",
            twitter: "",
            youtube: "",
            website: "",
            message: "",
            about: "",
            postTitle: "",
            postContent: "",
            btnTitle: "",
            btnUrl: "",
            postActivationDate: "",
            postExpiryDate: "",
            isInfluencer: false,
            comment: "",
        }
    });

    const selectedCountry = watch('country');

    // Prevent clearing `state` when we are populating the form initially from fetched data.
    // Only clear `state` when the user actually changes country after initial load.
    const initialCountrySet = React.useRef(true);
    useEffect(() => {
        if (initialCountrySet.current) {
            // first run (initialization) - skip clearing
            initialCountrySet.current = false;
            return;
        }
        setValue('state', '');
    }, [selectedCountry, setValue]);

    // Account Details fields configuration
    // Account fields: validation for these is handled only when pressing "Save & Register"
    const accountFields = [
        { label: 'Username', placeholder: 'Enter Username', name: 'username' },
        { label: 'Send Email To (Separate by commas)', placeholder: 'Enter Email Addresses', name: 'emailRecipients' },
    ];

    const onSubmit = async (data) => {
        if (!image) {
            toast.error("Please upload a profile picture.");
            return;
        }

        try {
            // Build a submission payload that always includes accountDetails.emails
            const emailsArray = data.emailRecipients
                ? data.emailRecipients.split(',').map(e => e.trim()).filter(e => e.length > 0)
                : (data.accountDetails?.emails || []);

            // Nest fields to match backend model shape: name, contactPerson, location, socialLinks, ambassadorDetails, accountDetails
            const submissionData = {
                // top-level simple fields
                email: data.email,
                // keep the original comma-separated string for backward-compatible backend handling
                emailRecipients: data.emailRecipients || (data.accountDetails?.emails ? data.accountDetails.emails.join(', ') : ''),
                username: data.username || undefined,
                mobile: data.mobile || undefined,
                dob: data.dob || undefined,
                gender: data.gender || undefined,
                active: active,
                isImagePresent: image ? true : false,

                // nested objects
                name: {
                    firstName: data.firstName || '',
                    lastName: data.lastName || ''
                },
                    // include profile picture in the payload data itself
                    profilepic: image || null,
                contactPerson: {
                    name: data.contactPersonName || '',
                    email: data.contactPersonEmail || '',
                    phone: data.contactPersonPhone || ''
                },
                location: {
                    country: data.country || '',
                    state: data.state || '',
                    city: data.city || '',
                    address: data.address || '',
                    pincode: data.pincode || '',
                    landmark: data.landmark || ''
                },
                socialLinks: {
                    linkedIn: data.linkedIn || '',
                    instagram: data.instagram || '',
                    facebook: data.facebook || '',
                    twitter: data.twitter || '',
                    youtube: data.youtube || '',
                    website: data.website || ''
                },
                ambassadorDetails: {
                    message: data.message || '',
                    about: data.about || '',
                    postTitle: data.postTitle || '',
                    postContent: data.postContent || '',
                    btnTitle: data.btnTitle || '',
                    btnUrl: data.btnUrl || '',
                    postActivationDate: data.postActivationDate || '',
                    postExpiryDate: data.postExpiryDate || '',
                    isInfluencer: data.isInfluencer || false,
                    comment: data.comment || ''
                },
                accountDetails: {
                    ...(data.accountDetails || {}),
                    // always send emails as an array so backend can persist them to accountDetails.emails
                    emails: emailsArray,
                }
            };

            // debug payload
            console.log('Submitting ambassador payload:', submissionData);

            if (id) {
                // Update existing ambassador
                toast.loading('Updating Green Ambassador...');
                // useUpdateAmbassador's mutate expects the raw data object (the hook will attach the id)
                await updateAmbassador(submissionData, {
                    onSuccess: (res) => {
                        console.log("Green Ambassador updated:", res);
                        toast.dismiss();
                        toast.success("Green Ambassador updated successfully!");
                        setTimeout(() => {
                            navigate('/user/list/ambassadors');
                        }, 2000);
                    }
                });
            } else {
                // Create new ambassador
                toast.loading('Creating Green Ambassador...');
                // useCreateAmbassador expects an object with `data` and optional `image`/`toast`
                await create({
                    data: submissionData,
                    image: image,
                    toast: toast
                }, {
                    onSuccess: (res) => {
                        console.log("Green Ambassador created:", res);
                        toast.dismiss();
                        toast.success("Green Ambassador created successfully!");
                        setTimeout(() => {
                            navigate('/user/list/ambassadors');
                        }, 2000);
                    }
                });
            }
        } catch (error) {
            toast.dismiss();
            console.error(id ? "Error updating ambassador:" : "Error creating ambassador:", error);
            toast.error(id ? "Error updating ambassador. Please try again." : "Error creating ambassador. Please try again.");
        }
    };

    const handleSaveAndSendCred = async () => {
        // Get current form values
        const formValues = getValues();

        // Validate account details fields - ALL are required for credentials
        const accountErrors = {};

        // Check username
        if (!formValues.username || formValues.username.trim() === '') {
            accountErrors.username = 'Username is required';
        } else if (formValues.username.length < 3) {
            accountErrors.username = 'Username must be at least 3 characters';
        }

        // // Check display name
        // if (!formValues.displayName || formValues.displayName.trim() === '') {
        //     accountErrors.displayName = 'Display Name is required';
        // } else if (formValues.displayName.length < 2) {
        //     accountErrors.displayName = 'Display name must be at least 2 characters';
        // }

        // Check email recipients
        if (!formValues.emailRecipients || formValues.emailRecipients.trim() === '') {
            accountErrors.emailRecipients = 'Email recipients are required';
        } else {
            // Validate email format
            const emails = formValues.emailRecipients.split(',').map(email => email.trim());
            const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
            const invalidEmails = emails.filter(email => email && !emailRegex.test(email));
            if (invalidEmails.length > 0) {
                accountErrors.emailRecipients = `Invalid email format: ${invalidEmails.join(', ')}`;
            }
        }

        // Check email (still required for primary contact)
        if (!formValues.email || formValues.email.trim() === '') {
            accountErrors.email = 'Email is required';
        } else {
            // Validate email format
            const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
            if (!emailRegex.test(formValues.email)) {
                accountErrors.email = 'Invalid email format';
            }
        }

        // If there are validation errors, set them and return
        if (Object.keys(accountErrors).length > 0) {
            // Set errors for each field
            Object.keys(accountErrors).forEach(field => {
                setError(field, {
                    type: 'manual',
                    message: accountErrors[field]
                });
            });

            // Show a toast message with specific error details
            toast.error('All Account Details fields are mandatory for sending credentials. Please fill all required fields correctly.');
            return;
        }

        // Clear any existing errors
        clearErrors(['username',/* 'displayName',*/ 'emailRecipients', 'email']);

        // Build nested payload similar to onSubmit so backend receives expected structure
        const emailsArray = formValues.emailRecipients
            ? formValues.emailRecipients.split(',').map(e => e.trim()).filter(e => e.length > 0)
            : [];

        const finalData = {
            email: formValues.email,
            username: formValues.username || undefined,
            mobile: formValues.mobile || undefined,
            dob: formValues.dob || undefined,
            gender: formValues.gender || undefined,
            active: active,
            isImagePresent: image ? true : false,
            name: {
                firstName: formValues.firstName || '',
                lastName: formValues.lastName || ''
            },
            contactPerson: {
                name: formValues.contactPersonName || '',
                email: formValues.contactPersonEmail || '',
                phone: formValues.contactPersonPhone || ''
            },
            location: {
                country: formValues.country || '',
                state: formValues.state || '',
                city: formValues.city || '',
                address: formValues.address || '',
                pincode: formValues.pincode || '',
                landmark: formValues.landmark || ''
            },
            socialLinks: {
                linkedIn: formValues.linkedIn || '',
                instagram: formValues.instagram || '',
                facebook: formValues.facebook || '',
                twitter: formValues.twitter || '',
                youtube: formValues.youtube || '',
                website: formValues.website || ''
            },
            ambassadorDetails: {
                message: formValues.message || '',
                about: formValues.about || '',
                postTitle: formValues.postTitle || '',
                postContent: formValues.postContent || '',
                btnTitle: formValues.btnTitle || '',
                btnUrl: formValues.btnUrl || '',
                postActivationDate: formValues.postActivationDate || '',
                postExpiryDate: formValues.postExpiryDate || '',
                isInfluencer: formValues.isInfluencer || false,
                comment: formValues.comment || ''
            },
            accountDetails: {
                id: id,
                username: formValues.username,
                ...(formValues.displayName ? { displayName: formValues.displayName } : {}),
                emails: emailsArray
            },
            profilepic: image || null
        };

        console.log('Processed data for save and send credentials:', finalData);

        // Show loading toast
        const loadingToastId = toast.loading('Saving Green Ambassador data and sending credentials...');

        // If validation passes, proceed with save and send credentials
        try {
            const result = await saveAndSendCredentials(finalData);
            
            // Dismiss loading toast
            toast.dismiss(loadingToastId);
            
            console.log('API Response:', result);
            
            if (result?.error) {
                toast.error('Something went wrong!');
                return;
            } else {
                toast.success('Green Ambassador data saved and credentials sent successfully!', {
                    duration: 3000,
                    position: 'top-right',
                });
                setTimeout(() => {
                    navigate('/user/list/ambassadors');
                }, 2000);
            }
        } catch (error) {
            // Dismiss loading toast
            toast.dismiss(loadingToastId);
            console.error('Error saving and sending credentials:', error);
            toast.error('Failed to save data and send credentials. Please try again.');
        }
    };    // Populate form fields when editing existing user
    React.useEffect(() => {
        if (userData) {
            setTimeout(() => {
                // Personal Information and Account Details
                setValue('firstName', userData.name?.firstName || '');
                setValue('lastName', userData.name?.lastName || '');
                setValue('username', userData.username || userData.accountDetails?.username || '');
                setValue('email', userData.email || '');
                setValue('emailRecipients', 
                    userData.emailRecipients || 
                    userData.accountDetails?.emails?.join(', ') || 
                    userData.emails?.join(', ') || 
                    ''
                );
                // Note: Don't populate password for security reasons
                setValue('mobile', userData.mobile || '');
                setValue('dob', userData.dob ? userData.dob.split('T')[0] : '');
                setValue('gender', userData.gender || '');
                setValue('contactPersonName', userData.contactPerson?.name || '');
                setValue('contactPersonEmail', userData.contactPerson?.email || '');
                setValue('contactPersonPhone', userData.contactPerson?.phone || '');

                // Location Information
                if (userData.location) {
                    const countryVal = userData.location.country || '';
                    const stateVal = userData.location.state || '';
                    setValue('country', countryVal);
                    // Set state after a short delay so the country update can propagate
                    // and the state dropdown's options are available for selection.
                    if (stateVal) {
                        setTimeout(() => setValue('state', stateVal), 50);
                    } else {
                        setValue('state', '');
                    }
                    setValue('city', userData.location.city || '');
                    setValue('address', userData.location.address || '');
                    setValue('pincode', userData.location.pincode || '');
                    setValue('landmark', userData.location.landmark || '');
                }

                // Social Links
                if (userData.socialLinks) {
                    setValue('linkedIn', userData.socialLinks.linkedIn || '');
                    setValue('instagram', userData.socialLinks.instagram || '');
                    setValue('facebook', userData.socialLinks.facebook || '');
                    setValue('twitter', userData.socialLinks.twitter || '');
                    setValue('youtube', userData.socialLinks.youtube || '');
                    setValue('website', userData.socialLinks.website || '');
                }

                // Ambassador Details
                if (userData.ambassadorDetails) {
                    setValue('message', userData.ambassadorDetails.message || '');
                    setValue('about', userData.ambassadorDetails.about || '');
                    setValue('postTitle', userData.ambassadorDetails.postTitle || '');
                    setValue('postContent', userData.ambassadorDetails.postContent || '');
                    setValue('btnTitle', userData.ambassadorDetails.btnTitle || '');
                    setValue('btnUrl', userData.ambassadorDetails.btnUrl || '');
                    setValue('isInfluencer', userData.ambassadorDetails.isInfluencer || false);
                    setValue('comment', userData.ambassadorDetails.comment || '');
                    setValue('isInfluencer', userData.ambassadorDetails.isInfluencer || false);
                    setValue('comment', userData.ambassadorDetails.comment || '');

                    setValue('postActivationDate', new Date(userData.ambassadorDetails.postActivationDate) || '');

                    console.log(userData.ambassadorDetails);
                    // Handle date format for date inputs (YYYY-MM-DD)
                    if (userData.ambassadorDetails.postActivationDate) {
                        const activationDate = new Date(userData.ambassadorDetails.postActivationDate);
                        setValue('postActivationDate', activationDate.toISOString().split('T')[0]);
                    }
                    if (userData.ambassadorDetails.postExpiryDate) {
                        const expiryDate = new Date(userData.ambassadorDetails.postExpiryDate);
                        setValue('postExpiryDate', expiryDate.toISOString().split('T')[0]);
                    }
                }

                // Set active status
                setActive(userData.active || false);

                // Set profile picture if available
                if (userData.profilepic) {
                    setImage(userData.profilepic);
                }
            }, 100);
        }
    }, [userData]);

    // Show loading state while fetching user data
    if (isLoadingUser) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <div className="text-lg">Loading Green Ambassador data...</div>
            </div>
        );
    }

    return (
        <form className="flex flex-col w-full font-semibold" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex justify-between">
                <Heading text={id ? "Edit Green Ambassador" : "Green Ambassador Form"} />
                <div className="flex gap-3 items-center">
                    <p className="text-base">Status:</p>
                    <div className={`flex justify-end items-center border ${active ? `border-primary` : `border-inactive`} rounded-md h-full min-h-8 min-w-20`}>
                        <label className="inline-flex items-center me-5 cursor-pointer h-full">
                            <span className={`ms-3 text-base ${active ? `text-primary` : 'text-inactive'} font-bold dark:text-gray-300 mr-2`}>{active ? 'Active' : 'Inactive'}</span>
                            <input type="checkbox" name={'active'} value={active} className="sr-only peer" onChange={() => setActive(!active)} checked={active} />
                            <div className="relative w-8 h-4 bg-white border-black ring-1 ring-inactive rounded-full peer dark:bg-white peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.1 after:start-[0px] after:bg-inactive after:border-inactive peer-checked:after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-navBtn peer-checked:ring-primary peer-checked:bg-primary"></div>
                        </label>
                    </div>
                </div>
            </div>

            {/* Personal Information Section */}
            <FormSection title="Personal Information" defaultExpanded={true}>
                <div className="flex gap-6">
                    <div className="flex flex-col w-full gap-4">
                        {/* Name Fields Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* First Name */}
                            <div className="flex flex-col w-full gap-1">
                                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                    <span className="text-red">*</span> First Name</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter First Name"
                                        {...register("firstName", { required: "First name is required" })}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.firstName ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.firstName && <p className="text-red text-sm">{errors.firstName.message}</p>}
                            </div>
                            {/* Last Name */}
                            <div className="flex flex-col w-full gap-1">
                                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize"><span className="text-red">*</span> Last Name</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter Last Name"
                                        {...register("lastName", { required: "Last name is required" })}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.lastName ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.lastName && <p className="text-red text-sm">{errors.lastName.message}</p>}
                            </div>
                        </div>

                        {/* Email and Mobile Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Email */}
                            <div className="flex flex-col w-full gap-1">
                                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize"><span className="text-red">*</span> Email</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="email"
                                        placeholder="Enter Email"
                                        {...register("email", {
                                            required: "Email is required",
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: "Invalid email address"
                                            }
                                        })}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.email ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.email && <p className="text-red text-sm">{errors.email.message}</p>}
                            </div>
                            {/* Mobile */}
                            <div className="flex flex-col w-full gap-1">
                                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Mobile Number</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="tel"
                                        placeholder="Enter Mobile Number"
                                        {...register("mobile")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.mobile ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.mobile && <p className="text-red text-sm">{errors.mobile.message}</p>}
                            </div>
                        </div>

                        {/* DOB and Gender Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Date of Birth */}
                            <div className="flex flex-col w-full gap-1">
                                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Date of Birth</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="date"
                                        placeholder="Enter Date of Birth"
                                        {...register("dob")}
                                        className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                    />
                                </div>
                            </div>
                            {/* Gender */}
                            <div className="flex flex-col w-full gap-1">
                                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Gender</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <select
                                        {...register("gender")}
                                        className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                    >
                                        <option value="">Choose Gender</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Contact Person Row */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Contact Person Name */}
                            <div className="flex flex-col w-full gap-1">
                                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Contact Person Name</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter Contact Person Name"
                                        {...register("contactPersonName")}
                                        className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                    />
                                </div>
                            </div>
                            {/* Contact Person Email */}
                            <div className="flex flex-col w-full gap-1">
                                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Contact Person Email</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="email"
                                        placeholder="Enter Contact Person Email"
                                        {...register("contactPersonEmail")}
                                        className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                    />
                                </div>
                            </div>
                            {/* Contact Person Phone */}
                            <div className="flex flex-col w-full gap-1">
                                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Contact Person Phone</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="tel"
                                        placeholder="Enter Contact Person Phone"
                                        {...register("contactPersonPhone")}
                                        className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                    />
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className="mt-0">
                        <MediaUpload
                            clearMedia={false}
                            existingImage={image}
                            setImageUpload={setImage}
                            stateImage={image}
                            setStateImageUpload={setImage}
                        />
                    </div>
                </div>

                {/* Address Information Subsection */}
                <div className="mt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Address Information</h3>
                    {/* Address Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Country */}
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Country</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <NewDropdown
                                    name="country"
                                    dataList={countryOptions}
                                    placeholder="Select Country"
                                    value={watch('country')}
                                    onChange={(selected) => setValue('country', selected.value)}
                                />
                            </div>
                        </div>
                        {/* State */}
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">State</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                {stateOptionsByCountry[selectedCountry] && stateOptionsByCountry[selectedCountry].length > 0 ? (
                                    <NewDropdown
                                        name="state"
                                        dataList={stateOptionsByCountry[selectedCountry]}
                                        placeholder="Select State"
                                        value={watch('state')}
                                        onChange={(selected) => setValue('state', selected.value)}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        placeholder="Enter State"
                                        {...register("state")}
                                        className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                    />
                                )}
                            </div>
                        </div>
                        {/* City */}
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">City</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder="Enter City"
                                    {...register("city")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                />
                            </div>
                        </div>
                    </div>
                    {/* Address, Pincode, Landmark */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Address */}
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Address</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder="Enter Address"
                                    {...register("address")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                />
                            </div>
                        </div>
                        {/* Pincode */}
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Pincode</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder="Enter Pincode"
                                    {...register("pincode")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                />
                            </div>
                        </div>
                        {/* Landmark */}
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Landmark</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder="Enter Nearest Landmark"
                                    {...register("landmark")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </FormSection>

            {/* Account Details Section */}
            <FormSection title="Account Details" defaultExpanded={false}>
                <p>*Note: Password will be auto generated from the system</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {accountFields.map((field, index) => (
                        <div key={field.name} className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                {field.label}
                            </label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder={field.placeholder}
                                    {...register(field.name, field.required ? {
                                        required: `${field.label} is required`,
                                        minLength: field.name === 'username' ? {
                                            value: 3,
                                            message: "Username must be at least 3 characters"
                                        } : field.name === 'displayName' ? {
                                            value: 2,
                                            message: "Display name must be at least 2 characters"
                                        } : undefined
                                    } : {})}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors[field.name] ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors[field.name] && <p className="text-red text-sm">{errors[field.name].message}</p>}
                        </div>
                    ))}
                    {/* Referral Code - Read Only (only shown when editing) */}
                    {id && (
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                Own Referral Code
                            </label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-gray-100 rounded-md border border-solid border-navBtn text-gray-700">
                                <input
                                    type="text"
                                    value={userData?.referralCode?.toUpperCase() || userData?.referral?.code?.toUpperCase() || 'N/A'}
                                    readOnly
                                    className="flex-1 shrink self-stretch my-auto basis-0 px-4 py-2.5 rounded-md text-base bg-gray-100 cursor-not-allowed font-bold tracking-wider"
                                />
                            </div>
                            <p className="text-gray-500 text-xs">This is the ambassador's unique referral code for inviting users</p>
                        </div>
                    )}
                </div>
            </FormSection>

            {/* Social Media Section */}
            <FormSection title="Social Media Details" defaultExpanded={false}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* LinkedIn */}
                    <div className="flex flex-col w-full gap-1">
                        <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">LinkedIn</label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <input
                                type="url"
                                placeholder="Enter LinkedIn Profile URL"
                                {...register("linkedIn", {
                                    pattern: {
                                        value: /^https?:\/\/.+/i,
                                        message: "Please enter a valid URL"
                                    }
                                })}
                                className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                            />
                        </div>
                    </div>
                    {/* Instagram */}
                    <div className="flex flex-col w-full gap-1">
                        <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Instagram</label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <input
                                type="url"
                                placeholder="Enter Instagram Profile URL"
                                {...register("instagram", {
                                    pattern: {
                                        value: /^https?:\/\/.+/i,
                                        message: "Please enter a valid URL"
                                    }
                                })}
                                className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                            />
                        </div>
                    </div>
                    {/* Facebook */}
                    <div className="flex flex-col w-full gap-1">
                        <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Facebook</label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <input
                                type="url"
                                placeholder="Enter Facebook Profile URL"
                                {...register("facebook", {
                                    pattern: {
                                        value: /^https?:\/\/.+/i,
                                        message: "Please enter a valid URL"
                                    }
                                })}
                                className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                            />
                        </div>
                    </div>
                    {/* Twitter */}
                    <div className="flex flex-col w-full gap-1">
                        <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Twitter</label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <input
                                type="url"
                                placeholder="Enter Twitter Profile URL"
                                {...register("twitter", {
                                    pattern: {
                                        value: /^https?:\/\/.+/i,
                                        message: "Please enter a valid URL"
                                    }
                                })}
                                className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                            />
                        </div>
                    </div>
                    {/* YouTube */}
                    <div className="flex flex-col w-full gap-1">
                        <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">YouTube</label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <input
                                type="url"
                                placeholder="Enter YouTube Channel URL"
                                {...register("youtube", {
                                    pattern: {
                                        value: /^https?:\/\/.+/i,
                                        message: "Please enter a valid URL"
                                    }
                                })}
                                className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                            />
                        </div>
                    </div>
                    {/* Website */}
                    <div className="flex flex-col w-full gap-1">
                        <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Website</label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <input
                                type="url"
                                placeholder="Enter Website URL"
                                {...register("website", {
                                    pattern: {
                                        value: /^https?:\/\/.+/i,
                                        message: "Please enter a valid URL"
                                    }
                                })}
                                className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                            />
                        </div>
                    </div>
                </div>
            </FormSection>

            {/* Ambassador Details Section */}
            <FormSection title="Ambassador Details" defaultExpanded={false}>
                <div className="flex flex-col w-full">
                    {/* <div className="flex gap-10 items-start w-full">
                        <div className="flex flex-wrap gap-10 items-start w-full">
                            <div className="flex flex-col grow shrink text-sm text-center text-gray-900 min-w-[240px] w-[437px]">
                                <div className="flex gap-2.5 items-center justify-end">
                                    <button type="button" className="overflow-hidden gap-2.5 px-5 py-2.5 rounded-md bg-navBtn">
                                        View Past Posts
                                    </button>
                                    <button type="button" className="overflow-hidden gap-2.5 px-5 py-2.5 rounded-md bg-navBtn">
                                        Archive A Post
                                    </button>
                                    <button type="button" className="flex overflow-hidden gap-2.5 justify-center items-center py-2.5 px-4 rounded-md bg-navBtn">
                                        <img
                                            loading="lazy"
                                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/4add1b29d90e78283ed735a5fe3ca3db354dd2df3922d9c175b1ba70a6ea1edd?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                                            alt=""
                                            className="object-contain shrink-0 self-stretch my-auto w-5 aspect-square"
                                        />
                                        <span className="self-stretch my-auto w-[93px]">
                                            Add New Post
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div> */}

                    {/* Message and About TextAreas */}
                    <div className="grid grid-cols-2 gap-6">
                        {/* Message TextArea */}
                        <div className="flex flex-col w-full gap-1 md:col-span-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Message</label>
                            <div className="flex gap-2.5 items-start mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <textarea
                                    placeholder="Enter message for the ambassador"
                                    {...register("message")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[120px] resize-vertical"
                                />
                            </div>
                        </div>

                        {/* About TextArea */}
                        <div className="flex flex-col w-full gap-1 md:col-span-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">About</label>
                            <div className="flex gap-2.5 items-start mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <textarea
                                    placeholder="Tell us about yourself"
                                    {...register("about")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[120px] resize-vertical"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Influencer Checkbox and Comment */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        {/* Influencer Checkbox */}
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                {...register("isInfluencer")}
                                className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary"
                            />
                            <label className="text-[14px] text-gray-900 font-[600]">Influencer?</label>
                        </div>
                        {/* Comment */}
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Comment</label>
                            <div className="flex gap-2.5 items-start mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <textarea
                                    placeholder="Enter any comments"
                                    {...register("comment")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[80px] resize-vertical"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Post Title and Content */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        {/* Post Title */}
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Post Title</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder="Enter post title"
                                    {...register("postTitle")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                />
                            </div>
                        </div>

                        {/* Post Content TextArea */}
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Post Content</label>
                            <div className="flex gap-2.5 items-start mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <textarea
                                    placeholder="Enter post content"
                                    {...register("postContent")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[100px] resize-vertical"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Button Title and URL Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Button Title</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder="Enter button title"
                                    {...register("btnTitle")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                />
                            </div>
                        </div>
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Button URL</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="url"
                                    placeholder="Enter button URL"
                                    {...register("btnUrl")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Post Dates Row */}
                    {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Post Activation Date</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    {...register("postActivationDate")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                />
                            </div>
                        </div>
                        <div className="flex flex-col w-full gap-1">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Post Expiry Date</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    {...register("postExpiryDate")}
                                    className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base"
                                />
                            </div>
                        </div>
                    </div> */}
                </div>
            </FormSection>

            <div className="flex mt-5 justify-between">
                <ActionButton
                    variant={'reset'}
                    text={'Reset Form'}
                    onClick={() => {
                        reset();
                        setImage(null);
                        setActive(true);
                    }}
                />
                <div className="flex gap-5">
                    <ActionButton
                        variant={'cancel'}
                        text={'Cancel'}
                        onClick={() => navigate('/user/list/ambassadors')}
                    />
                    <ActionButton
                        variant={'submit'}
                        text={isCreating || isUpdatingAmbassador ? (id ? 'Updating...' : 'Creating...') : (id ? 'Update' : 'Save')}
                        disabled={isCreating || isUpdatingAmbassador}
                        type="submit"
                    />
                    <ActionButton
                        variant={'submit'}
                        text={isSendingCredentials ? 'Sending...' : 'Save & Register'}
                        disabled={isSendingCredentials}
                        onClick={handleSaveAndSendCred}
                    />
                </div>
            </div>
            <ToastContainer position="top-right" autoClose={1000} />
        </form>
    );
}